import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontIcon } from '@fluentui/react';
import { useNavigate, useSearchParams } from 'react-router';
import SConsciousApiService from '../services/SConsciousApiService';
import ActionButton from './ActionButton';
import { useAppContext } from './AppContext';

const _api = new SConsciousApiService();

type IndexTab = 'product-items' | 'receipts' | 'merchants' | 'brands-corps' | 'corporations' | 'categories' | 'messages' | 'cards' | 'people' | 'recurring';

function localMatch(lower: string, ...fields: (string | undefined | null)[]): boolean {
    return fields.some(f => f && f.toLowerCase().includes(lower));
}

// Returns all searchable text values from an entity, skipping IDs and numeric fields
const TEXT_SKIP = new Set(['user_id', 'receipt_id', 'item_id', 'merchant_id', 'receipt_merchant_id', 'partitionKey', 'rowKey', 'timestamp', 'etag']);
function textFields(obj: Record<string, unknown>): string[] {
    return Object.entries(obj)
        .filter(([k, v]) => !TEXT_SKIP.has(k) && typeof v === 'string' && (v as string).length > 0 && (v as string).length < 500)
        .map(([, v]) => v as string);
}

function sortByDateDesc(results: SearchResult[]): SearchResult[] {
    return [...results].sort((a, b) => {
        const da = a.transaction_date || a.message_at || a.card_created || '';
        const db = b.transaction_date || b.message_at || b.card_created || '';
        return db.localeCompare(da);
    });
}

interface SearchResult {
    score?: number;
    // product-items
    item_id?: string;
    description?: string;
    product_type?: string;
    product_identifier?: string;
    brand?: string;
    merchant_name?: string;
    quantity?: number;
    total_price?: number;
    currency_code?: string;
    transaction_date?: string;
    // receipts
    receipt_id?: string;
    merchant_city?: string;
    total_amount?: number;
    // merchants
    merchant_id?: string;
    name?: string;
    city?: string;
    owning_corporation?: string;
    // brands-corps
    entity_name?: string;
    entity_type?: 'brand' | 'corporation';
    purchases?: string;
    total?: number;
    // corporations
    corp_name?: string;
    corp_purchases?: string;
    corp_total?: number;
    // categories
    category_name?: string;
    category_purchases?: number;
    category_total?: number;
    // friends
    friend_email?: string;
    friend_name?: string;
    friend_status?: string;
    friend_user_id?: string;
    // people
    person_user_id?: string;
    person_name?: string;
    person_email?: string;
    // messages
    message_id?: string;
    message_text?: string;
    message_from?: string;
    message_conv_id?: string;
    message_at?: string;
    // cards
    card_blob?: string;
    card_name?: string;
    card_url?: string;
    card_created?: string;
    // recurring
    recurring_cadence?: string;
    recurring_next_date?: string;
}

const TABS: { label: string; value: IndexTab }[] = [
    { label: 'Receipts', value: 'receipts' },
    { label: 'Recurring', value: 'recurring' },
    { label: 'Merchants', value: 'merchants' },
    { label: 'Items', value: 'product-items' },
    { label: 'Brands', value: 'brands-corps' },
    { label: 'Corporations', value: 'corporations' },
    { label: 'Categories', value: 'categories' },
    { label: 'People', value: 'people' },
    { label: 'Messages', value: 'messages' },
    { label: 'Cards', value: 'cards' },    
];

export default function Search() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const userId = localStorage.getItem('signed_in_id') || '';
    const { appData } = useAppContext();

    const ALL_TABS: IndexTab[] = ['product-items', 'receipts', 'merchants', 'brands-corps', 'corporations', 'categories', 'people', 'messages', 'cards', 'recurring'];
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const paramTab = searchParams.get('tab') as IndexTab | null;
    const paramQuery = searchParams.get('q') || '';
    const paramDq = searchParams.get('dq') || '';
    const [query, setQuery] = useState(paramQuery);
    const [dqFilter, setDqFilter] = useState(paramDq);
    const [tabResults, setTabResults] = useState<Partial<Record<IndexTab, SearchResult[]>>>({});
    const [activeTab, setActiveTab] = useState<IndexTab>(paramTab && ALL_TABS.includes(paramTab) ? paramTab : 'receipts');
    const [loading, setLoading] = useState(false);
    const [tabCounts, setTabCounts] = useState<Partial<Record<IndexTab, number>>>({});
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

    // Data-quality entity IDs: pre-compute which entities have the selected DQ issue
    const dqEntityIds = useMemo<Set<string> | null>(() => {
        if (!dqFilter) return null;
        switch (dqFilter) {
            case 'missing-brand':
                return new Set((appData.data.reportProductItems || []).filter(i => !i.brand || i.brand === 'Unknown').map(i => i.item_id));
            case 'missing-category':
                return new Set((appData.data.reportProductItems || []).filter(i => !i.product_type || i.product_type === 'Unknown').map(i => i.item_id));
            case 'zero-total':
                return new Set((appData.data.reportReceipts || []).filter(r => !r.total_amount || r.total_amount <= 0).map(r => r.receipt_id));
            case 'missing-date':
                return new Set((appData.data.reportReceipts || []).filter(r => !r.transaction_date).map(r => r.receipt_id));
            case 'missing-location': {
                const userMerchantIds = new Set((appData.data.reportReceipts || []).map(r => r.merchant_id).filter(Boolean));
                return new Set((appData.data.reportMerchants || []).filter(m => userMerchantIds.has(m.merchant_id) && (!m.latitude || !m.longitude)).map(m => m.merchant_id));
            }
            case 'total-mismatch': {
                const itemSumByReceipt = new Map<string, number>();
                (appData.data.reportProductItems || []).forEach((i: any) => {
                    if (!i.receipt_id) return;
                    itemSumByReceipt.set(i.receipt_id, (itemSumByReceipt.get(i.receipt_id) || 0) + (i.total_price || 0));
                });
                return new Set((appData.data.reportReceipts || []).filter(r => {
                    if (!r.receipt_id || !r.total_amount || r.total_amount <= 0) return false;
                    const itemSum = itemSumByReceipt.get(r.receipt_id);
                    if (itemSum == null) return false;
                    return Math.abs(itemSum - r.total_amount) > 0.02;
                }).map(r => r.receipt_id));
            }
            case 'orphaned-items': {
                const receiptIds = new Set((appData.data.reportReceipts || []).map(r => r.receipt_id).filter(Boolean));
                return new Set((appData.data.reportProductItems || []).filter(i => i.receipt_id && !receiptIds.has(i.receipt_id)).map(i => i.item_id));
            }
            case 'orphaned-receipts': {
                const merchantIds = new Set((appData.data.reportMerchants || []).map(m => m.merchant_id).filter(Boolean));
                return new Set((appData.data.reportReceipts || []).filter(r => r.merchant_id && !merchantIds.has(r.merchant_id)).map(r => r.receipt_id));
            }
            case 'orphaned-merchants': {
                const usedMerchantIds = new Set((appData.data.reportReceipts || []).map(r => r.merchant_id).filter(Boolean));
                return new Set((appData.data.reportMerchants || []).filter(m => !usedMerchantIds.has(m.merchant_id)).map(m => m.merchant_id));
            }
            case 'orphaned-brands': {
                const usedBrandNames = new Set((appData.data.reportProductItems || []).map(i => i.brand).filter(Boolean));
                return new Set((appData.data.brands || []).filter((b: any) => b.name && !usedBrandNames.has(b.name)).map((b: any) => b.name));
            }
            case 'orphaned-corporations': {
                const usedCorpNames = new Set((appData.data.brands || []).map((b: any) => b.owning_corporation).filter(Boolean));
                return new Set((appData.data.corporations || []).filter((c: any) => c.name && !usedCorpNames.has(c.name)).map((c: any) => c.name));
            }
            default:
                return null;
        }
    }, [dqFilter, appData]);

    // When DQ filter is active, compute filtered counts per tab
    // Only the applicable tab gets a count; all others are hidden
    const DQ_TAB_MAP: Record<string, IndexTab[]> = {
        'missing-brand': ['product-items'],
        'missing-category': ['product-items'],
        'zero-total': ['receipts'],
        'missing-date': ['receipts'],
        'missing-location': ['merchants'],
        'total-mismatch': ['receipts'],
        'orphaned-items': ['product-items'],
        'orphaned-receipts': ['receipts'],
        'orphaned-merchants': ['merchants'],
        'orphaned-brands': ['brands-corps'],
        'orphaned-corporations': ['corporations'],
    };
    const effectiveCounts = useMemo<Partial<Record<IndexTab, number>>>(() => {
        if (!dqFilter || !dqEntityIds) return tabCounts;
        const applicableTabs = new Set(DQ_TAB_MAP[dqFilter] || []);
        const filtered: Partial<Record<IndexTab, number>> = {};
        for (const tab of ALL_TABS) {
            if (!applicableTabs.has(tab)) continue;
            const results = tabResults[tab];
            if (!results) continue;
            filtered[tab] = results.filter(r => {
                if (tab === 'product-items') return dqEntityIds.has(r.item_id || '');
                if (tab === 'receipts') return dqEntityIds.has(r.receipt_id || '');
                if (tab === 'merchants') return dqEntityIds.has(r.merchant_id || '');
                if (tab === 'brands-corps') return dqEntityIds.has(r.entity_name || '');
                if (tab === 'corporations') return dqEntityIds.has(r.corp_name || '');
                return false;
            }).length;
        }
        return filtered;
    }, [dqFilter, dqEntityIds, tabCounts, tabResults]);

    // local-only data fetched once on mount
    const [friendsData, setFriendsData] = useState<any[]>([]);
    const [messagesData, setMessagesData] = useState<any[]>([]);  // flat list of all messages
    const [conversationsData, setConversationsData] = useState<any[]>([]);
    const [cardsData, setCardsData] = useState<any[]>([]);

    useEffect(() => {
        if (!userId) return;
        // fetch friends
        _api.getFriends(userId).then(f => setFriendsData(f || [])).catch(() => {});
        // fetch fidelity cards
        _api.listFidelityCards(userId).then(c => setCardsData(c || [])).catch(() => {});
        // fetch all conversations then all their messages
        _api.getChatConversations(userId).then(async convs => {
            setConversationsData(convs || []);
            const all: any[] = [];
            await Promise.all((convs || []).map(async (conv: any) => {
                try {
                    const msgs = await _api.getChatMessages(conv.conversationId, userId);
                    (msgs || []).forEach(m => all.push({ ...m, otherUserName: conv.otherUserName }));
                } catch { /* ignore */ }
            }));
            setMessagesData(all);
        }).catch(() => {});
    }, [userId]);

    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const tabScrollRef = useRef<HTMLDivElement>(null);

    const getNavTarget = useCallback((tab: IndexTab, r: SearchResult): (() => void) | null => {
        switch (tab) {
            case 'product-items': return r.item_id ? () => navigate(`/ProductItem?receiptId=${encodeURIComponent(String(r.receipt_id || ''))}&itemId=${encodeURIComponent(String(r.item_id))}`) : null;
            case 'receipts':      return r.receipt_id ? () => navigate(`/Receipt?receiptId=${r.receipt_id}`) : null;
            case 'merchants':     return r.merchant_id ? () => navigate(`/Merchant?merchantId=${r.merchant_id}`) : null;
            case 'brands-corps':  return r.entity_name ? () => navigate(`/Brand?brandId=${encodeURIComponent(r.entity_name!)}`) : null;
            case 'corporations':  return r.corp_name ? () => navigate(`/Corporation?corporationId=${encodeURIComponent(r.corp_name!)}`) : null;
            case 'categories':    return r.category_name ? () => navigate(`/Category?categoryId=${encodeURIComponent(r.category_name!)}`) : null;
            case 'cards':         return r.card_url ? () => setLightboxUrl(r.card_url!) : null;
            case 'people': {
                if (r.friend_user_id === 'support') return () => navigate('/Feedback');
                if (r.friend_user_id) return () => navigate(`/Profile?userId=${encodeURIComponent(r.friend_user_id!)}`);
                return r.person_user_id ? () => navigate(`/Profile?userId=${encodeURIComponent(r.person_user_id!)}`) : null;
            }
            case 'messages': {
                const conv = conversationsData.find((c: any) => c.conversationId === r.message_conv_id);
                return conv ? () => navigate('/Conversation', { state: { conv, backTo: '/Search' } }) : null;
            }
            default: return null;
        }
    }, [navigate, conversationsData, userId]);

    const searchOneTab = useCallback(async (
        q: string,
        index: IndexTab,
        semReceiptIds: Set<string> = new Set(),
        semMerchantIds: Set<string> = new Set(),
        prefetchedAI?: { results: any[]; count: number } | null
    ): Promise<void> => {
        const lower = q === '*' ? '' : q.toLowerCase();

        // ── Pure-local tabs ───────────────────────────────────────────────────
        if (index === 'corporations') {
            const results: SearchResult[] = (appData.data.corporations || [])
                .filter(c => !lower || localMatch(lower, ...textFields(c as unknown as Record<string, unknown>)))
                .map(c => ({ corp_name: c.name, corp_purchases: c.purchases, corp_total: c.total }));
            setTabResults(prev => ({ ...prev, [index]: sortByDateDesc(results) }));
            setTabCounts(prev => ({ ...prev, [index]: results.length }));
            return;
        }
        if (index === 'categories') {
            const normalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
            const categoryMap = new Map<string, { purchases: number; total: number }>();
            for (const item of (appData.data.reportProductItems || [])) {
                const raw = item.product_type || 'Uncategorised';
                const type = normalise(raw);
                if (!lower || type.toLowerCase().includes(lower)) {
                    const entry = categoryMap.get(type) || { purchases: 0, total: 0 };
                    entry.purchases += 1;
                    entry.total += item.total_price || 0;
                    categoryMap.set(type, entry);
                }
            }
            const results: SearchResult[] = Array.from(categoryMap.entries())
                .map(([name, data]) => ({
                    category_name: name,
                    category_purchases: data.purchases,
                    category_total: data.total,
                }))
                .sort((a, b) => (b.category_total || 0) - (a.category_total || 0));
            setTabResults(prev => ({ ...prev, [index]: results }));
            setTabCounts(prev => ({ ...prev, [index]: results.length }));
            return;
        }
        if (index === 'messages') {
            const results: SearchResult[] = messagesData
                .filter(m => !lower || localMatch(lower, m.message, m.fromName, m.otherUserName))
                .map(m => ({
                    message_id: m.messageId,
                    message_text: m.message,
                    message_from: m.fromName,
                    message_conv_id: m.conversationId,
                    message_at: m.createdAt,
                }));
            setTabResults(prev => ({ ...prev, [index]: sortByDateDesc(results) }));
            setTabCounts(prev => ({ ...prev, [index]: results.length }));
            return;
        }
        if (index === 'cards') {
            const results: SearchResult[] = cardsData
                .filter(c => !lower || localMatch(lower, c.name))
                .map(c => ({ card_blob: c.blobName, card_name: c.name, card_url: c.url, card_created: c.createdOn }));
            setTabResults(prev => ({ ...prev, [index]: sortByDateDesc(results) }));
            setTabCounts(prev => ({ ...prev, [index]: results.length }));
            return;
        }
        if (index === 'recurring') {
            const receipts = appData.data.reportReceipts || [];
            const merchants = appData.data.reportMerchants || [];
            const merchantMap = new Map(merchants.map(m => [m.merchant_id, m.name]));
            const results: SearchResult[] = receipts
                .filter((r: any) => r.recurring && r.recurring_next_date)
                .filter((r: any) => !lower || localMatch(lower, merchantMap.get(r.merchant_id) || '', r.receipt_type, r.recurring))
                .map((r: any) => ({
                    receipt_id: r.receipt_id,
                    merchant_name: merchantMap.get(r.merchant_id) || r.merchant_id,
                    total_amount: r.total_amount,
                    currency_code: r.currency_code,
                    transaction_date: r.transaction_date,
                    product_type: r.receipt_type,
                    recurring_cadence: r.recurring,
                    recurring_next_date: r.recurring_next_date,
                }));
            setTabResults(prev => ({ ...prev, [index]: sortByDateDesc(results) }));
            setTabCounts(prev => ({ ...prev, [index]: results.length }));
            return;
        }
        if (index === 'people') {
            // Always include friends (support entry + friend list)
            const supportEntry: SearchResult = {
                friend_email: 'support@sconscious.app',
                friend_name: 'sConscious Support',
                friend_status: 'support',
                friend_user_id: 'support',
            };
            const friendResults: SearchResult[] = friendsData
                .filter(f => (f.friend_name || f.friend_email) && (!lower || localMatch(lower, f.friend_name, f.friend_email, f.status)))
                .map(f => ({ friend_email: f.friend_email, friend_name: f.friend_name, friend_status: f.status, friend_user_id: f.friend_user_id }));

            // When there's a query, also search for other users
            let personResults: SearchResult[] = [];
            if (lower) {
                const friendUserIds = new Set(friendsData.map(f => f.friend_user_id).filter(Boolean));
                const users = await _api.searchUsers(q, userId);
                personResults = users
                    .filter(u => !friendUserIds.has(u.user_id))  // exclude people already in friends list
                    .map(u => ({
                        person_user_id: u.user_id,
                        person_name: u.user_name,
                        person_email: u.user_email,
                    }));
            }

            const results = [supportEntry, ...friendResults, ...personResults];
            setTabResults(prev => ({ ...prev, [index]: results }));
            setTabCounts(prev => ({ ...prev, [index]: results.length }));
            return;
        }
        if (index === 'merchants') {
            // Local-only: only show merchants the user has receipts for
            const userMerchantIds = new Set((appData.data.reportReceipts || []).map(r => r.merchant_id).filter(Boolean));
            const results: SearchResult[] = (appData.data.reportMerchants || [])
                .filter(m => userMerchantIds.has(m.merchant_id) && (!lower || localMatch(lower, ...textFields(m as unknown as Record<string, unknown>))))
                .map(m => ({ merchant_id: m.merchant_id, name: m.name, city: m.city, owning_corporation: m.owning_corporation }));
            setTabResults(prev => ({ ...prev, [index]: sortByDateDesc(results) }));
            setTabCounts(prev => ({ ...prev, [index]: results.length }));
            return;
        }
        if (index === 'brands-corps') {
            const lower = q === '*' ? '' : q.toLowerCase();
            const brandResults: SearchResult[] = (appData.data.brands || [])
                .filter(b => !lower || localMatch(lower, ...textFields(b as unknown as Record<string, unknown>)))
                .map(b => ({
                    entity_name: b.name,
                    entity_type: 'brand' as const,
                    owning_corporation: b.owning_corporation,
                    purchases: b.purchases,
                    total: b.total,
                }));
            setTabResults(prev => ({ ...prev, [index]: sortByDateDesc(brandResults) }));
            setTabCounts(prev => ({ ...prev, [index]: brandResults.length }));
            return;
        }

        const aiResult = prefetchedAI ?? await _api.searchAI(q, userId, index, 200);
        const aiResults: SearchResult[] = aiResult.results || [];
        let localExtra: SearchResult[] = [];

        if (lower) {
            if (index === 'product-items') {
                const aiIds = new Set(aiResults.map(r => r.item_id).filter(Boolean));
                localExtra = (appData.data.reportProductItems || [])
                    .filter(item =>
                        !aiIds.has(item.item_id) &&
                        localMatch(lower, ...textFields(item as unknown as Record<string, unknown>))
                    )
                    .map(item => ({
                        item_id: item.item_id,
                        receipt_id: item.receipt_id,
                        description: item.description,
                        product_type: item.product_type,
                        product_identifier: item.product_identifier,
                        brand: item.brand,
                        merchant_name: item.merchant_name,
                        quantity: item.quantity,
                        total_price: item.total_price,
                        currency_code: item.currency_code,
                        transaction_date: item.receipt_transaction_date,
                    }));
            } else if (index === 'receipts') {
                const aiIds = new Set(aiResults.map(r => r.receipt_id).filter(Boolean));
                // Direct field match on all receipt text fields
                const directReceiptIds = new Set(
                    (appData.data.reportReceipts || [])
                        .filter(r => localMatch(lower, ...textFields(r as unknown as Record<string, unknown>)))
                        .map(r => r.receipt_id)
                );
                // Receipts containing a locally-matched item (substring)
                const itemReceiptIds = new Set(
                    (appData.data.reportProductItems || [])
                        .filter(item => localMatch(lower, ...textFields(item as unknown as Record<string, unknown>)))
                        .map(item => item.receipt_id)
                );
                localExtra = (appData.data.reportReceipts || [])
                    .filter(r =>
                        !aiIds.has(r.receipt_id) &&
                        (directReceiptIds.has(r.receipt_id) || itemReceiptIds.has(r.receipt_id) || semReceiptIds.has(r.receipt_id))
                    )
                    .map(r => ({
                        receipt_id: r.receipt_id,
                        merchant_name: r.merchant_name,
                        merchant_city: r.merchant_city,
                        transaction_date: r.transaction_date,
                        total_amount: r.total_amount,
                        currency_code: r.currency_code,
                    }));

            } else if (index === 'merchants') {
                const aiIds = new Set(aiResults.map(r => r.merchant_id).filter(Boolean));
                // Direct field match on all merchant text fields
                const directMerchantIds = new Set(
                    (appData.data.reportMerchants || [])
                        .filter(m => localMatch(lower, ...textFields(m as unknown as Record<string, unknown>)))
                        .map(m => m.merchant_id)
                );
                // Merchants where a locally-matched item was bought
                const itemMerchantIds = new Set(
                    (appData.data.reportProductItems || [])
                        .filter(item => localMatch(lower, ...textFields(item as unknown as Record<string, unknown>)))
                        .map(item => item.receipt_merchant_id)
                        .filter(Boolean) as string[]
                );
                localExtra = (appData.data.reportMerchants || [])
                    .filter(m =>
                        !aiIds.has(m.merchant_id) &&
                        (directMerchantIds.has(m.merchant_id) || itemMerchantIds.has(m.merchant_id) || semMerchantIds.has(m.merchant_id))
                    )
                    .map(m => ({
                        merchant_id: m.merchant_id,
                        name: m.name,
                        city: m.city,
                        owning_corporation: m.owning_corporation,
                    }));
            }
        } else {
            // No query (wildcard *): supplement AI results with ALL local data so unindexed
            // data still appears.
            if (index === 'product-items') {
                const aiIds = new Set(aiResults.map(r => r.item_id).filter(Boolean));
                localExtra = (appData.data.reportProductItems || [])
                    .filter(item => !aiIds.has(item.item_id))
                    .map(item => ({
                        item_id: item.item_id,
                        receipt_id: item.receipt_id,
                        description: item.description,
                        product_type: item.product_type,
                        product_identifier: item.product_identifier,
                        brand: item.brand,
                        merchant_name: item.merchant_name,
                        quantity: item.quantity,
                        total_price: item.total_price,
                        currency_code: item.currency_code,
                        transaction_date: item.receipt_transaction_date,
                    }));
            } else if (index === 'receipts') {
                const aiIds = new Set(aiResults.map(r => r.receipt_id).filter(Boolean));
                localExtra = (appData.data.reportReceipts || [])
                    .filter(r => !aiIds.has(r.receipt_id))
                    .map(r => ({
                        receipt_id: r.receipt_id,
                        merchant_name: r.merchant_name,
                        merchant_city: r.merchant_city,
                        transaction_date: r.transaction_date,
                        total_amount: r.total_amount,
                        currency_code: r.currency_code,
                    }));
            }
        }

        const merged = [...aiResults, ...localExtra];
        setTabResults(prev => ({ ...prev, [index]: sortByDateDesc(merged) }));
        setTabCounts(prev => ({ ...prev, [index]: (aiResult.count ?? aiResults.length) + localExtra.length }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, friendsData, messagesData, cardsData]);

    const doSearchAll = useCallback(async (q: string) => {
        if (!userId) return;
        setLoading(true);

        // Step 1: fetch product-items via AI Search (vector similarity handles synonyms like
        // "coke" → "Coca-Cola"). Use those results to build semantic receipt/merchant ID sets
        // so receipts and merchants are surfaced even when their own fields don't contain the query.
        const semReceiptIds = new Set<string>();
        const semMerchantIds = new Set<string>();
        let itemsPrefetch: { results: any[]; count: number } | null = null;

        if (q !== '*') {
            try {
                itemsPrefetch = await _api.searchAI(q, userId, 'product-items', 500);
                const aiItemIds = new Set((itemsPrefetch.results || []).map((r: any) => r.item_id).filter(Boolean));
                (appData.data.reportProductItems || [])
                    .filter(item => aiItemIds.has(item.item_id))
                    .forEach(item => {
                        if (item.receipt_id) semReceiptIds.add(item.receipt_id);
                        if (item.receipt_merchant_id) semMerchantIds.add(item.receipt_merchant_id);
                    });
            } catch { /* ignore */ }
        }

        // Step 2: run all tabs in parallel, reusing the prefetched items result
        await Promise.all(
            (['product-items', 'receipts', 'merchants', 'brands-corps', 'corporations', 'categories', 'people', 'messages', 'cards', 'recurring'] as IndexTab[]).map(tab =>
                searchOneTab(
                    q, tab, semReceiptIds, semMerchantIds,
                    tab === 'product-items' ? itemsPrefetch : undefined
                ).catch(() => { setTabResults(prev => ({ ...prev, [tab]: [] })); })
            )
        );
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, searchOneTab]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const effectiveQuery = query.trim() || '*';
        const delay = query.trim() ? 350 : 0;
        debounceRef.current = setTimeout(() => doSearchAll(effectiveQuery), delay);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [query, doSearchAll, activeTab]);

    // Keep URL in sync so browser-back restores query + tab
    useEffect(() => {
        const params: Record<string, string> = { tab: activeTab };
        if (query.trim()) params.q = query.trim();
        if (dqFilter) params.dq = dqFilter;
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, activeTab, dqFilter]);

    return (
        <div style={{ background: '#ffcc00', minHeight: '100vh', fontFamily: 'inherit' }}>

            {/* Search bar */}
            <br />
            <div style={{ padding: '4px 16px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                    <FontIcon
                        iconName={query ? 'ChromeClose' : 'Search'}
                        onClick={query ? () => setQuery('') : undefined}
                        style={{
                            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                            fontSize: query ? 11 : 14, color: 'black',
                            cursor: query ? 'pointer' : 'default',
                            pointerEvents: query ? 'all' : 'none',
                        }}
                    />
                    <input
                        className="darkInput"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        style={{
                            width: '100%', paddingLeft: '2rem', boxSizing: 'border-box',
                            paddingRight: effectiveCounts[activeTab] != null ? '3.5rem' : undefined,
                        }}
                    />
                </div>
                <ActionButton
                    label=""
                    icon={<FontIcon iconName="Add" style={{ fontSize: '0.85rem' }} />}
                    onClick={() => navigate('/ManualReceipt')}
                    ariaLabel="Add receipt"
                    style={{ borderRadius: '50%', width: 32, height: 32, fontSize: '0.85rem', padding: 0, minWidth: 0 }}
                />
            </div>

            {/* Tabs — horizontally scrollable, no scrollbar, fade+arrow indicates more content */}
            <div style={{ position: 'relative', marginBottom: 12 }}>
                <div
                    ref={tabScrollRef}
                    onTouchStart={e => e.stopPropagation()}
                    style={{
                        display: 'flex', gap: 10, padding: '6px 16px 14px',
                        overflowX: 'auto', scrollbarWidth: 'none',
                        msOverflowStyle: 'none' as any,
                    }}
                    className="search-tab-scroll"
                >
                    {TABS.map(tab => (
                        <button
                            key={tab.value}
                            onClick={() => { setActiveTab(tab.value); }}
                            style={{
                                position: 'relative',
                                background: activeTab === tab.value ? '#000' : 'rgba(0,0,0,0.08)',
                                color: activeTab === tab.value ? '#ffcc00' : '#000',
                                border: 'none', borderRadius: 20, padding: '10px 20px',
                                cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                whiteSpace: 'nowrap', fontFamily: 'inherit', flexShrink: 0,
                                marginBottom: 8,
                            }}
                        >
                            {tab.label}
                            {effectiveCounts[tab.value] != null && (
                                <span style={{
                                    position: 'absolute', bottom: -6, right: -6,
                                    background: activeTab === tab.value ? '#ffcc00' : '#000',
                                    color: activeTab === tab.value ? '#000' : '#ffcc00',
                                    borderRadius: '50%', minWidth: 18, height: 18,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 10, fontWeight: 700, lineHeight: 1, padding: '0 3px',
                                    pointerEvents: 'none',
                                }}>
                                    {effectiveCounts[tab.value]}
                                </span>
                            )}
                    </button>
                ))}
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div style={{ textAlign: 'center', padding: 24, color: 'rgba(0,0,0,0.4)' }}>
                    <FontIcon iconName="ProgressRingDots" style={{ fontSize: 24 }} />
                </div>
            )}

            {/* Results */}
            {!loading && (() => {
                // Apply DQ filter if active
                let results = tabResults[activeTab] || [];
                if (dqFilter && dqEntityIds) {
                    results = results.filter(r => {
                        if (activeTab === 'product-items') return dqEntityIds.has(r.item_id || '');
                        if (activeTab === 'receipts') return dqEntityIds.has(r.receipt_id || '');
                        if (activeTab === 'merchants') return dqEntityIds.has(r.merchant_id || '');
                        if (activeTab === 'brands-corps') return dqEntityIds.has(r.entity_name || '');
                        if (activeTab === 'corporations') return dqEntityIds.has(r.corp_name || '');
                        return true;
                    });
                }
                return results.map((r, i) => {
                const onNav = getNavTarget(activeTab, r);
                return (
                <div
                    key={r.item_id || r.receipt_id || r.merchant_id || r.entity_name || r.category_name || r.person_user_id || i}
                    onClick={onNav ?? undefined}
                    style={{
                        background: 'rgba(0,0,0,0.08)', margin: '0 16px 8px',
                        borderRadius: 16, padding: '12px 14px', textAlign: 'left',
                        cursor: onNav ? 'pointer' : 'default',
                    }}
                >
                    {/* Items */}
                    {activeTab === 'product-items' && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, fontSize: 14, color: '#000', flex: 1, minWidth: 120 }}>
                                    {r.description}
                                </span>
                                {r.product_type && (
                                    <span style={{
                                        background: '#000', color: '#ffcc00',
                                        borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                                    }}>
                                        {r.product_type}
                                    </span>
                                )}
                                {r.brand && (
                                    <span style={{
                                        background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.6)',
                                        borderRadius: 20, padding: '2px 10px', fontSize: 11,
                                    }}>
                                        {r.brand}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: 'rgba(0,0,0,0.5)', fontSize: 12 }}>{r.merchant_name}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {r.quantity != null && r.quantity > 1 && (
                                        <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 11 }}>×{Math.round(r.quantity)}</span>
                                    )}
                                    {r.total_price != null && (
                                        <span style={{ color: '#000', fontWeight: 700, fontSize: 14 }}>
                                            {r.total_price.toFixed(2)} <span style={{ fontSize: 11, fontWeight: 400 }}>{r.currency_code}</span>
                                        </span>
                                    )}
                                </span>
                            </div>
                        </>
                    )}

                    {/* Receipts */}
                    {activeTab === 'receipts' && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, fontSize: 14, color: '#000' }}>{r.merchant_name}</span>
                                {r.total_amount != null && (
                                    <span style={{ color: '#000', fontWeight: 700, fontSize: 14 }}>
                                        {r.total_amount.toFixed(2)} <span style={{ fontSize: 11, fontWeight: 400 }}>{r.currency_code}</span>
                                    </span>
                                )}
                            </div>
                            <div style={{ color: 'rgba(0,0,0,0.5)', fontSize: 12 }}>
                                {r.merchant_city}{r.merchant_city && r.transaction_date ? ' · ' : ''}{r.transaction_date?.substring(0, 10)}
                            </div>
                        </>
                    )}

                    {/* Merchants */}
                    {activeTab === 'merchants' && (
                        <>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#000', marginBottom: 4 }}>{r.name}</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {r.city && (
                                    <span style={{
                                        background: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.6)',
                                        borderRadius: 20, padding: '2px 10px', fontSize: 11,
                                    }}>
                                        {r.city}
                                    </span>
                                )}
                                {r.owning_corporation && (
                                    <span style={{
                                        background: '#000', color: '#ffcc00',
                                        borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                                    }}>
                                        {r.owning_corporation}
                                    </span>
                                )}
                            </div>
                        </>
                    )}

                    {/* Brands & Corps */}
                    {activeTab === 'brands-corps' && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, fontSize: 14, color: '#000', flex: 1 }}>{r.entity_name}</span>
                                <span style={{
                                    background: r.entity_type === 'brand' ? '#000' : 'rgba(0,0,0,0.15)',
                                    color: r.entity_type === 'brand' ? '#ffcc00' : '#000',
                                    borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                                }}>
                                    {r.entity_type === 'brand' ? 'Brand' : 'Corporation'}
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 12, color: 'rgba(0,0,0,0.5)', fontSize: 12 }}>
                                {r.owning_corporation && <span>Corp: {r.owning_corporation}</span>}
                                {r.purchases && <span>{r.purchases} purchases</span>}
                                {r.total != null && <span>{r.total.toFixed(2)}</span>}
                            </div>
                        </>
                    )}

                    {/* Corporations */}
                    {activeTab === 'corporations' && (
                        <>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#000', marginBottom: 4 }}>{r.corp_name}</div>
                            <div style={{ display: 'flex', gap: 12, color: 'rgba(0,0,0,0.5)', fontSize: 12 }}>
                                {r.corp_purchases && <span>{r.corp_purchases} purchases</span>}
                                {r.corp_total != null && <span>{r.corp_total!.toFixed(2)}</span>}
                            </div>
                        </>
                    )}

                    {/* Categories */}
                    {activeTab === 'categories' && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, fontSize: 14, color: '#000', flex: 1 }}>{r.category_name}</span>
                                {r.category_purchases != null && (
                                    <span style={{
                                        background: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.55)',
                                        borderRadius: 20, padding: '2px 10px', fontSize: 11,
                                    }}>
                                        {r.category_purchases} items
                                    </span>
                                )}
                            </div>
                            {r.category_total != null && (
                                <div style={{ color: '#000', fontWeight: 700, fontSize: 14 }}>
                                    {r.category_total.toFixed(2)}
                                </div>
                            )}
                        </>
                    )}

                    {/* People (friends + discovered users) */}
                    {activeTab === 'people' && (
                        <>
                            {r.friend_user_id ? (
                                /* Friend entry */
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <span style={{ fontWeight: 600, fontSize: 14, color: '#000', flex: 1 }}>
                                            {r.friend_name || r.friend_email}
                                        </span>
                                        <span style={{
                                            background: '#000', color: '#ffcc00',
                                            borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                                        }}>
                                            friend
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                                        {r.friend_status === 'registered' ? 'registered' : r.friend_status}
                                    </div>
                                </>
                            ) : (
                                /* Discovered person */
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontWeight: 600, fontSize: 14, color: '#000', flex: 1 }}>
                                            {r.person_name || r.person_email}
                                        </span>
                                        <span style={{
                                            background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.6)',
                                            borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                                        }}>
                                            person
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12, visibility: 'hidden' }}>&nbsp;</div>
                                </>
                            )}
                        </>
                    )}

                    {/* Messages */}
                    {activeTab === 'messages' && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{r.message_from}</span>
                                <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.35)' }}>
                                    {r.message_at ? new Date(r.message_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : ''}
                                </span>
                            </div>
                            <div style={{ fontSize: 13, color: '#000', lineClamp: 2, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {r.message_text}
                            </div>
                        </>
                    )}

                    {/* Cards */}
                    {activeTab === 'cards' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {r.card_url && (
                                <img src={r.card_url} alt={r.card_name || 'card'} style={{
                                    width: 60, height: 38, objectFit: 'cover', borderRadius: 6, flexShrink: 0,
                                    border: '1px solid rgba(0,0,0,0.1)',
                                }} />
                            )}
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#000' }}>{r.card_name}</div>
                                {r.card_created && (
                                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
                                        Added {new Date(r.card_created).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Recurring */}
                    {activeTab === 'recurring' && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontWeight: 600, fontSize: 14, color: '#000', flex: 1 }}>{r.merchant_name}</span>
                                <span style={{
                                    background: '#000', color: '#ffcc00',
                                    borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                                }}>
                                    {r.recurring_cadence}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    {r.product_type && (
                                        <span style={{ color: 'rgba(0,0,0,0.5)', fontSize: 12 }}>{r.product_type}</span>
                                    )}
                                    {r.recurring_next_date && (
                                        <span style={{ color: 'rgba(0,0,0,0.4)', fontSize: 11, marginLeft: 8 }}>Next: {r.recurring_next_date}</span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {r.total_amount != null && (
                                        <span style={{ color: '#000', fontWeight: 700, fontSize: 14 }}>
                                            {r.total_amount.toFixed(2)} <span style={{ fontSize: 11, fontWeight: 400 }}>{r.currency_code}</span>
                                        </span>
                                    )}
                                    <button
                                        className="actionButton"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            if (!r.receipt_id || cancellingId) return;
                                            setCancellingId(r.receipt_id);
                                            const ok = await _api.cancelRecurring(r.receipt_id, userId);
                                            setCancellingId(null);
                                            if (ok) {
                                                setTabResults(prev => ({
                                                    ...prev,
                                                    recurring: (prev.recurring || []).filter(x => x.receipt_id !== r.receipt_id),
                                                }));
                                                setTabCounts(prev => ({ ...prev, recurring: Math.max(0, (prev.recurring || 0) - 1) }));
                                            }
                                        }}
                                        style={{
                                            fontFamily: 'inherit', fontSize: '0.72rem', padding: '0.2rem 0.5rem',
                                            background: 'rgba(180,0,0,0.12)', color: '#900', border: 'none',
                                            opacity: cancellingId === r.receipt_id ? 0.5 : 1,
                                        }}
                                    >
                                        {cancellingId === r.receipt_id ? '…' : 'Cancel'}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                );})})()}

            {/* DQ filter banner */}
            {dqFilter && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    margin: '0 16px 8px', padding: '8px 14px',
                    background: 'rgba(144,0,0,0.10)', borderRadius: 12, fontSize: 12, color: '#900', fontWeight: 600,
                }}>
                    <FontIcon iconName="Warning" style={{ fontSize: 14 }} />
                    Showing: {dqFilter.replace(/-/g, ' ')}
                    <button
                        onClick={() => setDqFilter('')}
                        style={{
                            background: 'rgba(0,0,0,0.08)', border: 'none', borderRadius: '50%',
                            width: 20, height: 20, cursor: 'pointer', fontSize: 11,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontFamily: 'inherit', color: '#900', fontWeight: 700,
                        }}
                    >✕</button>
                </div>
            )}

            {/* Empty state */}
            {!loading && (tabResults[activeTab] || []).length === 0 && effectiveCounts[activeTab] != null && (
                <div style={{ textAlign: 'center', color: 'rgba(0,0,0,0.35)', padding: 48, fontFamily: 'inherit' }}>
                    <FontIcon iconName="SearchIssue" style={{ fontSize: 36, display: 'block', marginBottom: 12, color: 'rgba(0,0,0,0.2)' }} />
                    {query ? `No results for "${query}"` : 'No results found'}
                </div>
            )}

            {/* Card lightbox */}
            {lightboxUrl && (
                <div
                    onClick={() => setLightboxUrl(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' }}
                >
                    <img src={lightboxUrl} alt="fidelity card" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 10, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} />
                    <button
                        className="closeBtn"
                        onClick={e => { e.stopPropagation(); setLightboxUrl(null); }}
                        style={{ position: 'absolute', top: 16, right: 16 }}
                    >✕</button>
                </div>
            )}
        </div>
    );
}

